// THE WIRE ENVELOPE a runner publishes on `fleet.evt.<run_id>` — deliberately NOT the
// persisted row: the mothership derives the chain fields itself.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { TaskIdSchema } from '../ids';
import { TaskRunEventKind, TaskRunEventKindSchema } from './kind';

/**
 * What a fleet runner publishes on `fleet.evt.<runner_id>.<run_id>.<kind>` — the
 * CONTENT of one event, before the mothership persists it. snake_case: it crosses
 * NATS verbatim, same convention as `FleetTaskAssignment` / `TaskEnvelope`.
 *
 * NO publisher field, deliberately (#1951). The runnerId is the SUBJECT's leading
 * token, which the box's credential pins to its own id — a body field would be
 * whatever the sender typed, and the sink would have two answers for one question.
 *
 * Deliberately NOT `TaskRunEventSchema`: that is the PERSISTED row, which also
 * carries the DB-minted `id` + `ts` and the mothership-computed `prev_hash`
 * chain link. A runner knows none of those — it sends its `run_id`, the owning
 * `task_id` (from the assignment envelope), the `kind`, and an optional
 * `payload`; the event-sink mints `id`/`ts` and computes `prev_hash` at persist
 * time. `payload` is an object (or null) so it round-trips through jsonb and
 * `insertTaskRunEvent`; consumers narrow on `kind` before reading it.
 */
export const FleetRunEventSchema = z
  .object({
    /** Runner-minted execution run id — the `fleet.evt.<runner_id>.<run_id>` stream key. */
    run_id: z.string().min(1),
    /**
     * Task this run belongs to (carried in the assignment envelope). Required for
     * EVERY coding-run kind (the `superRefine` below enforces it — the persist chain
     * is per task). Absent ONLY for a task-less `ci_log` report: a CI jit-exec run
     * has no `tasks` row, and its report is recorded to `ci_jobs` (keyed on
     * `ci_job_id`), never chained into `task_run_events`.
     */
    task_id: TaskIdSchema.optional(),
    kind: TaskRunEventKindSchema,
    /**
     * The FENCE TOKEN the box was assigned (`FleetTaskAssignment.lease_epoch` →
     * `tasks.lease_epoch`), echoed verbatim on every event of the run. The
     * mothership requires it to match the task's CURRENT generation on the terminal
     * write, so a box still executing a task that was re-queued and re-claimed
     * elsewhere is fenced out instead of clobbering the live run (arc 101 doc 03).
     *
     * REQUIRED wherever there is a task to fence — the `superRefine` below pins it
     * to exactly the same condition as `task_id`, because they are the same
     * condition: a lease is a generation OF a task. It was optional-⇒-unfenced,
     * which is a fence with an opt-out written into its own schema; a box that
     * simply omitted the field got the clobber the fence exists to prevent, and
     * nothing on the mothership could tell that from a legitimately task-less event.
     */
    lease_epoch: z.number().int().nonnegative().optional(),
    /** Event body; null/absent for content-free events (e.g. a bare status ping). */
    payload: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .superRefine((event, ctx) => {
    // `ci_log` is the sole task-less kind; any other kind without a task_id would
    // fork the persist chain onto a missing task — reject it at the wire boundary.
    if (event.kind === TaskRunEventKind.CiLog) return;
    if (event.task_id === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `task_id is required for a '${event.kind}' run event`,
        path: ['task_id'],
      });
    }
    // ...and so is the fence token, at the SAME boundary and on the same
    // condition. A CI jit-exec run has no `tasks` row and therefore no generation
    // to fence against, which is why the carve-out is the kind and not the field:
    // "the sender left it out" must never be a way to arrive unfenced.
    if (event.lease_epoch === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `lease_epoch is required for a '${event.kind}' run event (the fence is not optional)`,
        path: ['lease_epoch'],
      });
    }
  });
export type FleetRunEvent = z.infer<typeof FleetRunEventSchema>;
